const KEYS = {
  TOKEN: "verify_token",
  DESTINATION: "verify_destination",
  EXPIRES_AT: "verify_expires_at",
  OTP_EXPIRES_AT: "verify_otp_expires_at",
  RESEND_AVAILABLE_AT: "verify_resend_available_at",
  SOURCE: "verify_source",
  USER: "verify_user",
};

const LEGACY_EMAIL_KEY = "pending_verify_email";
const LEGACY_TOKEN_KEY = "pending_verify_token";
const LEGACY_USER_KEY = "pending_verify_user";
const LEGACY_TEMP_TOKEN = "temp_token";

function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    // ignore
  }
}

function clearLegacyStorage() {
  [LEGACY_EMAIL_KEY, LEGACY_TOKEN_KEY, LEGACY_USER_KEY, LEGACY_TEMP_TOKEN].forEach(
    (key) => {
      safeRemove(localStorage, key);
      safeRemove(sessionStorage, key);
    }
  );
}

function toIntSeconds(val, fallback = 60) {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.max(1, Math.ceil(n));
}

export function getPendingVerifyToken() {
  return (
    safeGet(sessionStorage, KEYS.TOKEN) ||
    safeGet(sessionStorage, LEGACY_TOKEN_KEY) ||
    safeGet(localStorage, LEGACY_TEMP_TOKEN) ||
    null
  );
}

export function getPendingVerifyEmail() {
  return (
    safeGet(sessionStorage, KEYS.DESTINATION) ||
    safeGet(sessionStorage, LEGACY_EMAIL_KEY) ||
    safeGet(localStorage, LEGACY_EMAIL_KEY) ||
    ""
  );
}

export function getVerifyDestination() {
  return getPendingVerifyEmail();
}

export function getVerifySource() {
  return safeGet(sessionStorage, KEYS.SOURCE) || "register";
}

export function getVerifyUser() {
  const raw = safeGet(sessionStorage, KEYS.USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function isVerifyTokenExpired() {
  const iso = safeGet(sessionStorage, KEYS.EXPIRES_AT);
  if (!iso) return false;
  const expiresAt = new Date(iso).getTime();
  if (!Number.isFinite(expiresAt)) return false;
  return Date.now() >= expiresAt;
}

export function hasPendingVerification() {
  return !!getPendingVerifyToken() && !isVerifyTokenExpired();
}

export function getResendCooldownSeconds() {
  const at = Number(safeGet(sessionStorage, KEYS.RESEND_AVAILABLE_AT));
  if (!Number.isFinite(at) || at <= 0) return 0;
  return Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

export function getOtpExpirySeconds() {
  const iso = safeGet(sessionStorage, KEYS.OTP_EXPIRES_AT);
  if (!iso) return null;
  const expiresAt = new Date(iso).getTime();
  if (!Number.isFinite(expiresAt)) return null;
  const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
  return remaining > 0 ? remaining : 0;
}

export function setVerifySessionFromApi(body, source = "register") {
  const token = body?.token;
  if (!token) return false;

  const destination = body?.destination || body?.data?.email || "";
  const retryAfter = toIntSeconds(body?.retry_after, 60);
  const expiresAt = body?.expires_at || "";
  const otpExpiresAt = body?.verification_expires_at || "";

  safeSet(sessionStorage, KEYS.TOKEN, token);
  if (destination) safeSet(sessionStorage, KEYS.DESTINATION, destination);
  if (expiresAt) safeSet(sessionStorage, KEYS.EXPIRES_AT, expiresAt);
  if (otpExpiresAt) safeSet(sessionStorage, KEYS.OTP_EXPIRES_AT, otpExpiresAt);
  safeSet(
    sessionStorage,
    KEYS.RESEND_AVAILABLE_AT,
    String(Date.now() + retryAfter * 1000)
  );
  safeSet(sessionStorage, KEYS.SOURCE, source);

  if (body?.data) {
    safeSet(sessionStorage, KEYS.USER, JSON.stringify(body.data));
  }

  clearLegacyStorage();
  return true;
}

export function updateVerifySessionFromResend(body) {
  const retryAfter = toIntSeconds(body?.retry_after, 60);

  if (body?.verification_expires_at) {
    safeSet(sessionStorage, KEYS.OTP_EXPIRES_AT, body.verification_expires_at);
  }
  if (body?.destination) {
    safeSet(sessionStorage, KEYS.DESTINATION, body.destination);
  }

  safeSet(
    sessionStorage,
    KEYS.RESEND_AVAILABLE_AT,
    String(Date.now() + retryAfter * 1000)
  );
}

export function setPendingVerification({ email, token, retry_after, expires_at, verification_expires_at, destination }) {
  return setVerifySessionFromApi(
    {
      token,
      destination: destination || email,
      retry_after,
      expires_at,
      verification_expires_at,
    },
    "register"
  );
}

export function clearPendingVerification() {
  Object.values(KEYS).forEach((key) => safeRemove(sessionStorage, key));
  clearLegacyStorage();
}

export function maskEmail(email) {
  const value = String(email || "").trim();
  if (!value || !value.includes("@")) return value;

  const [local, domain] = value.split("@");
  if (!local || !domain) return value;
  if (local.length <= 2) return `**@${domain}`;

  return `${local[0]}***${local.slice(-1)}@${domain}`;
}
